import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { SaveIcon, CancelIcon } from "./icons";
import { avatarUrl } from "../utils/cloudinary";

function EditBureauProfileModal({ isOpen, onClose, bureau, onSaved }) {
  const [formData, setFormData] = useState({
    name: bureau?.name || "",
    ownerName: bureau?.ownerName || "",
    contactNumber: bureau?.contactNumber || "",
    email: bureau?.email || "",
    address: bureau?.address || "",
    password: "",
    confirmPassword: "",
  });
  const [imagePreview, setImagePreview] = useState(bureau?.image || null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  useEffect(() => {
    setFormData({
      name: bureau?.name || "",
      ownerName: bureau?.ownerName || "",
      contactNumber: bureau?.contactNumber || "",
      email: bureau?.email || "",
      address: bureau?.address || "",
      password: "",
      confirmPassword: "",
    });
    setImagePreview(bureau?.image || null);
    setSelectedFile(null);
    setError("");
    setFileError("");
    setImageOffset({ x: 0, y: 0 });
    setIsDragging(false);
    setDragStart(null);
    setImageZoom(1);
  }, [bureau, isOpen]);

  useEffect(() => {
    if (!imagePreview || !selectedFile) return;

    const image = new Image();
    image.src = imagePreview;
    image.onload = () => {
      const naturalWidth = image.width;
      const naturalHeight = image.height;
      const previewSize = 200;
      const scale = Math.max(previewSize / naturalWidth, previewSize / naturalHeight);
      const width = naturalWidth * scale * imageZoom;
      const height = naturalHeight * scale * imageZoom;
      setImageSize({ width, height });
      setImageOffset({
        x: (previewSize - width) / 2,
        y: (previewSize - height) / 2,
      });
    };
  }, [imagePreview, selectedFile, imageZoom]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getClampedOffset = (offsetX, offsetY, width, height, previewSize) => {
    let x = offsetX;
    let y = offsetY;
    if (width > previewSize) {
      x = Math.min(0, Math.max(previewSize - width, x));
    } else {
      x = (previewSize - width) / 2;
    }
    if (height > previewSize) {
      y = Math.min(0, Math.max(previewSize - height, y));
    } else {
      y = (previewSize - height) / 2;
    }
    return { x, y };
  };

  const handleImagePointerDown = (e) => {
    if (!imagePreview) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY, originX: imageOffset.x, originY: imageOffset.y });
  };

  const handleImagePointerMove = (e) => {
    if (!isDragging || !dragStart) return;
    e.preventDefault();
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    const previewSize = 200;
    const { x, y } = getClampedOffset(
      dragStart.originX + dx,
      dragStart.originY + dy,
      imageSize.width,
      imageSize.height,
      previewSize
    );
    setImageOffset({ x, y });
  };

  const handleImagePointerUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  const handleZoom = (direction) => {
    setImageZoom(prev => {
      const newZoom = direction === 'in' ? prev + 0.1 : prev - 0.1;
      return Math.max(1, Math.min(3, newZoom));
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFileError("Only image files are allowed.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFileError("Image must be smaller than 5MB.");
      return;
    }

    setFileError("");
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = (event) => {
      setImagePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const getCroppedImageBlob = (file, displaySize, offset, previewSize) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const url = URL.createObjectURL(file);
      image.onload = async () => {
        try {
          const scale = displaySize.width / image.width;
          const sourceX = Math.max(0, -offset.x) / scale;
          const sourceY = Math.max(0, -offset.y) / scale;
          const sourceWidth = previewSize / scale;
          const sourceHeight = previewSize / scale;

          const canvas = document.createElement('canvas');
          canvas.width = previewSize;
          canvas.height = previewSize;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            previewSize,
            previewSize
          );

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create cropped image'));
            }
          }, file.type || 'image/jpeg');
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      image.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(new Error('Unable to load image for cropping'));
      };
      image.src = url;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password && formData.password !== formData.confirmPassword) {
      setError("Password and confirm password must match.");
      return;
    }

    setLoading(true);
    setGlobalLoading(true);
    setGlobalLoadingMessage("Updating bureau profile...");

    try {
      const body = new FormData();
      body.append("name", formData.name);
      body.append("ownerName", formData.ownerName);
      body.append("contactNumber", formData.contactNumber);
      body.append("email", formData.email);
      body.append("address", formData.address);
      if (formData.password) {
        body.append("password", formData.password);
      }
      if (selectedFile) {
        const croppedBlob = await getCroppedImageBlob(selectedFile, imageSize, imageOffset, 200);
        body.append("image", croppedBlob, selectedFile.name);
      }

      const response = await apiFetch("/auth/profile", {
        method: "PUT",
        body,
      });

      onSaved(response.message || "Profile updated successfully");
      onClose();
    } catch (err) {
      console.error("Edit bureau profile failed:", err);
      setError(err.message || "Failed to update profile.");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl">
        {/* HEADER — always visible */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--color-border)] flex-shrink-0">
          <h2 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)]">Edit Bureau Profile</h2>
          <button
            onClick={onClose}
            className="text-2xl font-bold text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* BODY — scrolls */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {error && (
            <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-4 py-3 text-sm text-[var(--color-danger-text)]">
              {error}
            </div>
          )}

          {/* Profile image — centered at the top of the body. */}
          <div className="flex flex-col items-center gap-3">
            {imagePreview && selectedFile ? (
              <>
                <div className="relative w-40 h-40 rounded-full overflow-hidden border-2 border-[var(--color-border)] bg-[var(--color-background)]">
                  <div
                    className="absolute inset-0 cursor-grab touch-none"
                    onPointerDown={handleImagePointerDown}
                    onPointerMove={handleImagePointerMove}
                    onPointerUp={handleImagePointerUp}
                    onPointerLeave={handleImagePointerUp}
                  >
                    <img
                      src={imagePreview}
                      alt="Profile preview"
                      className="absolute"
                      style={{
                        left: `${imageOffset.x}px`,
                        top: `${imageOffset.y}px`,
                        width: `${imageSize.width}px`,
                        height: `${imageSize.height}px`,
                      }}
                      draggable={false}
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 rounded-full border border-dashed border-[var(--color-border)]" />
                </div>
                <p className="text-[11px] text-[var(--color-text-muted)] text-center">
                  Drag image to position the crop area
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    type="button"
                    onClick={() => handleZoom('out')}
                    disabled={imageZoom <= 1}
                    className="px-3 py-1 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    −
                  </button>
                  <span className="flex items-center px-3 text-sm text-[var(--color-text-muted)]">
                    {(imageZoom * 100).toFixed(0)}%
                  </span>
                  <button
                    type="button"
                    onClick={() => handleZoom('in')}
                    disabled={imageZoom >= 3}
                    className="px-3 py-1 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    +
                  </button>
                </div>
              </>
            ) : (
              <div className="w-32 h-32 rounded-full overflow-hidden border border-[var(--color-border)] bg-[var(--color-border)]">
                {imagePreview ? (
                  <img
                    // 128px container; helper requests 256px@2x from Cloudinary
                    // with face-aware square crop + f_auto,q_auto. Local
                    // data: previews (when a new file is being picked but the
                    // crop branch hasn't activated yet) pass through unchanged.
                    src={avatarUrl(imagePreview, 128)}
                    alt="Preview"
                    className="w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[var(--color-surface)] text-sm text-[var(--color-text-muted)]">
                    No image
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col items-center gap-1.5 w-full">
              <label
                htmlFor="bureau-image-input"
                className="inline-flex items-center gap-2 cursor-pointer rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                {selectedFile ? "Change Image" : "Upload Profile Image"}
              </label>
              <input
                id="bureau-image-input"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="sr-only"
              />
              {selectedFile && (
                <p className="text-[11px] text-[var(--color-text-muted)] text-center truncate max-w-xs">
                  {selectedFile.name}
                </p>
              )}
              {fileError && (
                <p className="text-sm text-red-500 text-center">{fileError}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Bureau Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Owner Name</label>
              <input
                type="text"
                name="ownerName"
                value={formData.ownerName}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Contact Number</label>
              <input
                type="text"
                name="contactNumber"
                value={formData.contactNumber}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">New Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Leave blank to keep current password"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-text-muted)]">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat new password"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text-strong)] focus:border-[var(--color-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--color-border-strong)]"
              />
            </div>
          </div>

          {/* Image block moved to the top of the body — see above. */}

          </div>
          {/* FOOTER — always visible */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              <CancelIcon className="w-4 h-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:cursor-not-allowed disabled:opacity-60 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <SaveIcon className="w-4 h-4" />
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditBureauProfileModal;
